<template>
  <div v-if="rows.length >= 0">
    <b-card title="Listado de pedidos">
      <b-button
        class="ml-2 float-right"
        variant="primary"
        type="submit"
        :to="{ name: 'admin-create-order'}"
      >
        Crear pedido
      </b-button>

      <!-- search input -->
      <div class="custom-search d-flex justify-content-end">
        <b-form-group>
          <div class="d-flex align-items-center">
            <label class="mr-1">Buscar</label>
            <b-form-input
              v-model="searchTerm"
              placeholder="Buscar"
              type="text"
              class="d-inline-block"
            />
          </div>
        </b-form-group>
      </div>

      <!-- table -->
      <vue-good-table
        :columns="columns"
        :rows="rows"
        :rtl="direction"
        :search-options="{
          enabled: true,
          externalQuery: searchTerm }"
        :select-options="{
          enabled: false,
          selectOnCheckboxOnly: true, // only select when checkbox is clicked instead of the row
          selectionInfoClass: 'custom-class',
          selectionText: 'rows selected',
          clearSelectionText: 'clear',
          disableSelectInfo: true, // disable the select info panel on top
          selectAllByGroup: true, // when used in combination with a grouped table, add a checkbox in the header row to check/uncheck the entire group
        }"
        :pagination-options="{
          enabled: true,
          perPage:pageLength
        }"
      >
        <template
          slot="table-row"
          slot-scope="props"
        >

          <!-- Column: Name -->
          <span
            v-if="props.column.field === 'fullName'"
            class="text-nowrap"
          >
            <b-avatar
              :src="props.row.avatar"
              class="mx-1"
            />
            <span class="text-nowrap">{{ props.row.fullName }}</span>
          </span>

          <!-- Column: Status -->
          <span v-else-if="props.column.field === 'status'">
            <b-badge
              v-if="props.row.planning_order_status"
              :variant="statusVariant(props.row.planning_order_status.name)"
            >
              {{ props.row.planning_order_status.name }}
            </b-badge>
          </span>

          <span v-else-if="props.column.field === 'is_paid'">
            <b-badge
              :variant="props.row.planning_order.is_paid ? 'light-success' : 'light-warning'"
            >
              {{ props.row.planning_order.is_paid ? 'Pago realizado' : 'Pago pendiente' }}
            </b-badge>
          </span>

          <!-- Column: Action -->
          <span v-else-if="props.column.field === 'action'">
            <span>

              <b-button
                v-if="props.row.planning_order"
                v-b-tooltip.hover.top="'Ver detalle'"
                variant="flat-primary"
                class="btn-icon"
                title="Detalle"
                :to="{name: 'admin-planning-order-detail', params: { id: props.row.planning_order.id } }"
              >
                <feather-icon
                  icon="EyeIcon"
                  class="text-success"
                  size="18"
                />
              </b-button>

              <!--              <b-dropdown
                variant="link"
                toggle-class="text-decoration-none"
                no-caret
              >
                <template v-slot:button-content>
&lt;!&ndash;                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="16"
                    class="text-body align-middle mr-25"
                  />&ndash;&gt;

                </template>
&lt;!&ndash;                <b-dropdown-item
                  v-if="props.row.planning_order"
                  :to="{name: 'admin-planning-order-detail', params: { id: props.row.planning_order.id } }"
                >
                  <feather-icon
                    icon="EyeIcon"
                    class="mr-50"
                  />
                  <span>Detalle</span>
                </b-dropdown-item>
                <b-dropdown-item>
                  <feather-icon
                    icon="TrashIcon"
                    class="mr-50"
                  />
                  <span>Delete</span>
                </b-dropdown-item>&ndash;&gt;
              </b-dropdown>-->
            </span>
          </span>

          <!-- Column: Created at -->
          <span v-else-if="props.column.field === 'created_at'">
            {{ formatDateVariant(props.row.created_at) }}
          </span>

          <!-- Column: Common -->
          <span v-else>
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>

        <!-- pagination -->
        <template
          slot="pagination-bottom"
          slot-scope="props"
        >
          <div class="d-flex justify-content-between flex-wrap">
            <div class="d-flex align-items-center mb-0 mt-1">
              <span class="text-nowrap ">
                Mostrando 1 a
              </span>
              <b-form-select
                v-model="pageLength"
                :options="['10','20','50']"
                class="mx-1"
                @input="(value)=>props.perPageChanged({currentPerPage:value})"
              />
              <span class="text-nowrap"> de {{ props.total }} items </span>
            </div>
            <div>
              <b-pagination
                :value="1"
                :total-rows="props.total"
                :per-page="pageLength"
                first-number
                last-number
                align="right"
                prev-class="prev-item"
                next-class="next-item"
                class="mt-1 mb-0"
                @input="(value)=>props.pageChanged({currentPage:value})"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>
            </div>
          </div>
        </template>
      </vue-good-table>

    </b-card>
  </div>

  <loading-skeleton v-else />
</template>

<script>
import BCardCode from '@core/components/b-card-code/BCardCode.vue'
import {
  BAvatar, BBadge, BPagination, BFormGroup, BFormInput, BFormSelect, BDropdown, BDropdownItem, BButton, BCard, VBTooltip,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import store from '@/store/index'
import api from '@/plugin/axios'
import { formatDate } from '@/helpers/utils'
import LoadingSkeleton from '@/views/misc/LoadingSkeleton.vue'
import Ripple from 'vue-ripple-directive'

export default {
  components: {
    LoadingSkeleton,
    BButton,
    BCard,
    VueGoodTable,
    BAvatar,
    BBadge,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BDropdown,
    BDropdownItem,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      pageLength: 10,
      dir: false,
      columns: [
        {
          label: 'ID',
          field: 'id',
        },
        {
          label: 'Clínica',
          field: 'clinic.user.first_name',
        },
        {
          label: 'Odontólogo',
          field: 'dentist.full_name',
        },
        {
          label: 'Paciente',
          field: 'patient.full_name',
        },
        {
          label: 'Fecha de solicitud',
          field: 'created_at',
        },
        {
          label: 'Fecha de entrega',
          field: 'delivery_date',
        },
        {
          label: 'Ciudad',
          field: 'clinic.city.name',
        },
        {
          label: 'Estado',
          field: 'status',
        },
        {
          label: 'Pago',
          field: 'is_paid',
        },
        {
          label: 'Acciones',
          field: 'action',
        },
      ],
      rows: [],
      searchTerm: '',
      status: [{
        1: 'Current',
        2: 'Professional',
        3: 'Rejected',
        4: 'Resigned',
        5: 'Applied',
      },
      {
        1: 'light-primary',
        2: 'light-success',
        3: 'light-danger',
        4: 'light-warning',
        5: 'light-info',
      }],
    }
  },
  computed: {
    statusVariant() {
      const statusColor = {
        /* eslint-disable key-spacing */
        Enviado      : 'light-primary',
        Planificación: 'light-info',
        Evaluación   : 'light-warning',
        Fabricación  : 'light-success',
        Terminado    : 'light-secondary',
        Transporte   : 'light-dark',
        Entregado    : 'light-success',
        /* eslint-enable key-spacing */
      }

      return status => statusColor[status]
    },
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true
        return this.dir
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false
      return this.dir
    },
    formatDateVariant() {
      return date => formatDate(date)
    },

  },
  async created() {
    const [rows] = await Promise.all([
      api.get('/api/orders'),
    ])

    this.rows = rows.data
    console.log(this.rows)
  },
}
</script>
